// Persist in-flight regeneration intents across page refreshes. The fetch
// itself dies with the page navigation, so on mount ReviewTest reads this
// state and re-fires the regenerate API calls. The TTL caps how long an
// orphaned entry can live (e.g., user abandoned the tab) before it's
// dropped on next load.

import type { Question } from "@shared/schema";

const STORAGE_KEY = "testcraft.activeRegenerations";
const TTL_MS = 5 * 60 * 1000; // 5 minutes

export interface PendingQuestionRegen {
  questionId: string;
  startedAt: number;
  // Confirmed-level regeneration: replay must reproduce the level the teacher
  // selected and the blueprint decision, not silently rerun at the old level.
  targetLevel?: Question["level"];
  blueprintDecision?: "update" | "keep";
}

export interface PendingAnswerRegen {
  questionId: string;
  answerIndex: number;
  startedAt: number;
}

export interface PersistedRegenState {
  questions: PendingQuestionRegen[];
  answers: PendingAnswerRegen[];
}

const empty: PersistedRegenState = { questions: [], answers: [] };

const read = (): PersistedRegenState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty;
    const parsed = JSON.parse(raw);
    return {
      questions: Array.isArray(parsed.questions) ? parsed.questions : [],
      answers: Array.isArray(parsed.answers) ? parsed.answers : [],
    };
  } catch {
    return empty;
  }
};

const write = (state: PersistedRegenState): void => {
  try {
    if (state.questions.length === 0 && state.answers.length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    }
  } catch {
    // Quota or unavailable storage — silently drop, the in-memory state
    // still drives the UX during the active session.
  }
};

// Drop entries older than TTL. Used on read so callers always see a
// fresh-enough view of pending work.
const pruneStale = (state: PersistedRegenState): PersistedRegenState => {
  const cutoff = Date.now() - TTL_MS;
  return {
    questions: state.questions.filter((q) => q.startedAt >= cutoff),
    answers: state.answers.filter((a) => a.startedAt >= cutoff),
  };
};

export const loadPendingRegens = (): PersistedRegenState => {
  const pruned = pruneStale(read());
  // Rewrite if we dropped anything so subsequent reads don't re-prune.
  write(pruned);
  return pruned;
};

export const markQuestionPending = (
  questionId: string,
  meta?: { targetLevel?: Question["level"]; blueprintDecision?: "update" | "keep" },
): void => {
  const state = pruneStale(read());
  if (state.questions.some((q) => q.questionId === questionId)) return;
  state.questions.push({ questionId, startedAt: Date.now(), ...(meta ?? {}) });
  write(state);
};

export const unmarkQuestionPending = (questionId: string): void => {
  const state = pruneStale(read());
  state.questions = state.questions.filter((q) => q.questionId !== questionId);
  write(state);
};

export const markAnswerPending = (questionId: string, answerIndex: number): void => {
  const state = pruneStale(read());
  if (state.answers.some((a) => a.questionId === questionId && a.answerIndex === answerIndex)) return;
  state.answers.push({ questionId, answerIndex, startedAt: Date.now() });
  write(state);
};

export const unmarkAnswerPending = (questionId: string, answerIndex: number): void => {
  const state = pruneStale(read());
  state.answers = state.answers.filter(
    (a) => !(a.questionId === questionId && a.answerIndex === answerIndex),
  );
  write(state);
};

export const clearAllPendingRegens = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
};
